import React, { useState, useEffect } from "react";
import { Bitcoin, Lock, ShieldCheck, LoaderCircle, CircleCheck, ArrowRight, MessageCircleMore, CalendarClock, RefreshCcw } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { useLanguage } from "../../context/LanguageContext";

const METHODS = [
  { id: "card", logos: ["/logos/logo_visa.png", "/logos/logo_mastercard.png"] },
  { id: "yappy", logos: ["/logos/logo_yappy.png"] },
  { id: "crypto", icon: Bitcoin },
];

const AMOUNT = "$89.00";

const MethodTab = ({ m, label, active, onClick }) => (
  <button
    type="button"
    data-testid={`checkout-method-${m.id}`}
    onClick={onClick}
    className={`flex flex-col items-center justify-center gap-2 rounded-2xl border-2 px-3 py-4 transition-all duration-200 ${
      active ? "border-[#0fb013] bg-[#0fe814]/10" : "border-black/10 bg-white hover:border-black/25"
    }`}
  >
    <div className="flex items-center gap-2 h-6">
      {m.icon ? (
        <m.icon size={22} className="text-[#f7931a]" />
      ) : (
        m.logos.map((src) => <img key={src} src={src} alt="" className="h-[16px] w-auto object-contain" draggable="false" />)
      )}
    </div>
    <span className="text-[11px] font-bold text-black/70">{label}</span>
  </button>
);

const Field = ({ label, value, className = "" }) => (
  <div className={className}>
    <div className="text-[10px] font-bold uppercase tracking-wider text-black/40">{label}</div>
    <div className="mt-1 px-3.5 py-2.5 rounded-xl bg-[#fafcf9] border border-black/10 text-[13px] font-semibold text-black/80 font-mono">{value}</div>
  </div>
);

const MethodForm = ({ method, c }) => {
  if (method === "card") {
    return (
      <div className="grid grid-cols-2 gap-3">
        <Field label={c.cardNumber} value="4242 4242 4242 4242" className="col-span-2" />
        <Field label={c.expiry} value="12 / 28" />
        <Field label={c.cvv} value="•••" />
      </div>
    );
  }
  if (method === "yappy") {
    return (
      <div className="rounded-2xl border border-black/10 bg-[#fafcf9] p-5 text-center">
        <img src="/logos/logo_yappy.png" alt="Yappy" className="h-[22px] mx-auto" draggable="false" />
        <p className="mt-3 text-[13px] font-medium text-black/60">{c.yappyHint}</p>
        <div className="mt-3 inline-block px-4 py-2 rounded-xl bg-white border border-black/10 text-[14px] font-bold font-mono text-black">+507 6xxx-xxxx</div>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-black/10 bg-[#fafcf9] p-5 text-center">
      <div className="mx-auto w-12 h-12 rounded-full bg-[#f7931a]/15 flex items-center justify-center">
        <Bitcoin size={24} className="text-[#f7931a]" />
      </div>
      <p className="mt-3 text-[13px] font-medium text-black/60">{c.cryptoHint}</p>
      <div className="mt-3 flex items-center justify-center gap-2 flex-wrap">
        {["USDT", "USDC", "BTC", "ETH"].map((k) => (
          <span key={k} className="px-3 py-1 rounded-full bg-white border border-black/10 text-[11px] font-bold text-black/70">{k}</span>
        ))}
      </div>
    </div>
  );
};

const PLANS = { monthly: { price: 49, months: 1 }, quarterly: { price: 132, months: 3 }, yearly: { price: 470, months: 12 } };
const LOCALES = { es: "es-PA", en: "en-US", pt: "pt-BR" };

const CheckoutDemo = ({ open, onOpenChange, scenario, plan = "monthly" }) => {
  const { t, lang } = useLanguage();
  const c = t.checkoutDemo;
  const isCollection = scenario === "collection";
  const isSubscription = scenario === "subscription";
  const planInfo = PLANS[plan] || PLANS.monthly;
  const amount = isCollection ? "$120.00" : isSubscription ? `$${planInfo.price.toFixed(2)}` : AMOUNT;
  const defaultEmail = isCollection ? "maria@correo.com" : "cliente@correo.com";
  const renewalDate = new Date();
  renewalDate.setMonth(renewalDate.getMonth() + planInfo.months);
  const renewalLabel = renewalDate.toLocaleDateString(LOCALES[lang] || "es-PA", { day: "numeric", month: "long", year: "numeric" });
  const [method, setMethod] = useState("card");
  const [step, setStep] = useState("form");
  const [email, setEmail] = useState(defaultEmail);

  useEffect(() => {
    if (open) { setStep("form"); setMethod("card"); setEmail(defaultEmail); }
  }, [open, defaultEmail]);

  useEffect(() => {
    if (step !== "processing") return;
    const id = setTimeout(() => setStep("success"), 1800);
    return () => clearTimeout(id);
  }, [step]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="checkout-demo-modal" className="max-w-md p-0 overflow-hidden rounded-3xl border-0 bg-white">
        <DialogTitle className="sr-only">{c.title}</DialogTitle>
        <DialogDescription className="sr-only">{c.subtitle}</DialogDescription>

        <div className="bg-black px-6 py-5 flex items-center justify-between">
          <img src="/logos/rapidpay-white.png" alt="RapidPay" className="h-[26px] w-auto" draggable="false" />
          <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#0fe814] bg-[#0fe814]/10 px-2.5 py-1 rounded-full">
            <Lock size={11} /> {c.secure}
          </span>
        </div>

        {step === "success" ? (
          <div data-testid="checkout-success" className="px-6 py-10 text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-[#0fe814] flex items-center justify-center shadow-[0_8px_24px_rgba(15,232,20,0.4)]">
              <CircleCheck size={34} className="text-black" strokeWidth={2.5} />
            </div>
            <h3 className="mt-5 font-extrabold text-[24px] text-black">{c.successTitle}</h3>
            <p className="mt-2 text-[14px] text-black/55 font-medium">{c.successDesc}</p>
            <div className="mt-6 rounded-2xl border border-black/10 bg-[#fafcf9] p-4 text-left space-y-2">
              <div className="flex justify-between text-[13px]"><span className="text-black/50 font-medium">{c.amount}</span><span className="font-bold text-black">{amount}</span></div>
              <div className="flex justify-between text-[13px]"><span className="text-black/50 font-medium">{c.reference}</span><span className="font-bold font-mono text-black">RP-4F9A2C</span></div>
              <div className="flex justify-between text-[13px]"><span className="text-black/50 font-medium">{c.method}</span><span className="font-bold text-black">{c.methods[method]}</span></div>
              <div className="flex justify-between text-[13px]"><span className="text-black/50 font-medium">{c.email}</span><span className="font-bold text-black truncate ml-3">{email}</span></div>
              {isSubscription && (
                <>
                  <div className="flex justify-between text-[13px]"><span className="text-black/50 font-medium">{c.subscription.label}</span><span className="font-bold text-black">Plan Pro · {c.plans[plan]}</span></div>
                  <div className="flex justify-between text-[13px]"><span className="text-black/50 font-medium">{c.subscription.nextRenewal}</span><span className="font-bold text-black">{renewalLabel}</span></div>
                </>
              )}
            </div>
            <button
              type="button"
              data-testid="checkout-close-btn"
              onClick={() => onOpenChange(false)}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 text-[14px] font-bold text-black bg-[#0fe814] hover:bg-[#0fb013] py-3.5 rounded-full transition-colors duration-200"
            >
              {c.close} <ArrowRight size={16} />
            </button>
          </div>
        ) : (
          <div className="px-6 py-6">
            {isCollection && (
              <div data-testid="checkout-collection-banner" className="mb-5 flex items-start gap-3 rounded-2xl bg-[#25D366]/10 border border-[#25D366]/30 px-4 py-3">
                <span className="mt-0.5 shrink-0 w-7 h-7 rounded-full bg-[#25D366] flex items-center justify-center">
                  <MessageCircleMore size={15} className="text-white" />
                </span>
                <div className="min-w-0">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-black/45">{c.collection.label}</div>
                  <div className="text-[13px] font-semibold text-black/80 leading-snug">{c.collection.text}</div>
                  <div className="mt-1 text-[11px] font-bold text-[#ff603e]">{c.collection.due}</div>
                </div>
              </div>
            )}
            {isSubscription && (
              <div data-testid="checkout-subscription-banner" className="mb-5 rounded-2xl bg-[#fafcf9] border border-black/[0.08] px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-black/45">{c.subscription.label}</div>
                    <div className="text-[14px] font-bold text-black">Plan Pro · {c.plans[plan]}</div>
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-black bg-[#0fe814] px-3 py-1 rounded-full whitespace-nowrap">
                    <RefreshCcw size={12} /> {c.subscription.auto}
                  </span>
                </div>
                <div className="mt-2.5 flex items-center gap-2 text-[12px] font-semibold text-black/70">
                  <CalendarClock size={14} className="text-[#0fb013]" />
                  {c.subscription.nextRenewal}: <span data-testid="checkout-renewal-date" className="font-bold text-black">{renewalLabel}</span>
                </div>
              </div>
            )}
            <div className="flex items-end justify-between">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-black/40">{c.merchant}</div>
                <div className="text-[15px] font-bold text-black">{isCollection ? c.collection.merchant : isSubscription ? c.subscription.merchant : c.merchantName}</div>
              </div>
              <div className="text-right">
                <div className="text-[11px] font-bold uppercase tracking-wider text-black/40">{c.total}</div>
                <div data-testid="checkout-amount" className="text-[26px] font-extrabold text-black leading-none">{amount}</div>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-3 gap-2.5">
              {METHODS.map((m) => (
                <MethodTab key={m.id} m={m} label={c.methods[m.id]} active={method === m.id} onClick={() => setMethod(m.id)} />
              ))}
            </div>

            <div className="mt-5">
              <div className="text-[10px] font-bold uppercase tracking-wider text-black/40">{c.email}</div>
              <input
                type="email"
                data-testid="checkout-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={c.emailPh}
                className="mt-1 w-full px-3.5 py-2.5 rounded-xl bg-white border border-black/10 text-[13px] font-semibold text-black/80 focus:border-[#0fb013] focus:outline-none transition-colors"
              />
            </div>

            <div className="mt-4">
              <MethodForm method={method} c={c} />
            </div>

            <button
              type="button"
              data-testid="checkout-pay-btn"
              disabled={step === "processing"}
              onClick={() => setStep("processing")}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 text-[15px] font-bold text-black bg-[#0fe814] hover:bg-[#0fb013] disabled:opacity-80 py-4 rounded-full transition-colors duration-200 shadow-[0_6px_20px_rgba(15,232,20,0.35)]"
            >
              {step === "processing" ? (
                <><LoaderCircle size={18} className="animate-spin" /> {c.processing}</>
              ) : (
                <>{c.pay} {amount}</>
              )}
            </button>

            <div className="mt-4 flex items-center justify-center gap-4 text-[11px] font-semibold text-black/40">
              <span className="inline-flex items-center gap-1"><ShieldCheck size={13} /> PCI DSS</span>
              <span className="inline-flex items-center gap-1"><Lock size={13} /> 3-D Secure</span>
            </div>
            <p className="mt-3 text-center text-[10px] font-medium text-black/35">{c.demoNote}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CheckoutDemo;
